/**
 * Edit Preference Modal
 *
 * Modal for editing customer preference information including:
 * - Invoicee (contact selection)
 * - Shipping Instruction
 * - Default Shipping Mark Front
 * - Default Shipping Mark Side
 *
 * Uses React Hook Form + matches vendor modal styling patterns
 * Data comes from CustomerPaymentResponse (payment API)
 */

import { useMemo, useEffect } from 'react';
import { Modal, Select, Input, Typography, theme, Row, Col } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useTranslation } from 'react-i18next';
import { useCustomerPaymentMutations } from '@/hooks/useCustomerPayment';
import type {
  CustomerDetailResponse,
  CustomerContactResponse,
  CustomerPaymentResponse,
} from '@/types/api/customer';

const { Text } = Typography;
const { TextArea } = Input;

interface EditPreferenceFormData {
  invoiceeId: number | null;
  shippingInstruction: string;
  defaultShippingMarkFront: string;
  defaultShippingMarkSide: string;
}

export interface EditPreferenceModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  customer: CustomerDetailResponse | null;
  contacts: CustomerContactResponse[];
  paymentData: CustomerPaymentResponse | null;
}

export const EditPreferenceModal: React.FC<EditPreferenceModalProps> = ({
  open,
  onCancel,
  onSuccess,
  customer,
  contacts,
  paymentData,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useTranslation('customers');
  const { handleError, handleSuccess } = useErrorHandler();

  // Use mutation hook for updating payment data
  const { updatePaymentData } = useCustomerPaymentMutations(customer?.customerId ?? 0);

  // Compute form values from payment data (memoized to prevent unnecessary re-renders)
  const formValues = useMemo(() => {
    if (!paymentData) {
      return {
        invoiceeId: null,
        shippingInstruction: '',
        defaultShippingMarkFront: '',
        defaultShippingMarkSide: '',
      };
    }

    return {
      invoiceeId: paymentData.invoiceeContactId ?? null,
      shippingInstruction: paymentData.shippingInstruction ?? '',
      defaultShippingMarkFront: paymentData.defaultShippingMarkFront ?? '',
      defaultShippingMarkSide: paymentData.defaultShippingMarkSide ?? '',
    };
  }, [paymentData]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditPreferenceFormData>({
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  const handleFormSubmit = async (data: EditPreferenceFormData) => {
    if (!customer || !paymentData) return;

    try {
      // Update payment data with preference fields
      // Keep existing payment fields and only update preference-related ones
      await updatePaymentData({
        // Preserve existing required fields
        primaryCurrency: paymentData.primaryCurrency,
        // Tax fields are now flat (not nested object)
        taxTypeId: paymentData.taxTypeId ?? 1,
        // Preserve existing payment fields
        priceLevelId: paymentData.priceLevelId ?? undefined,
        defaultReceivablesAccount: paymentData.defaultReceivablesAccount ?? undefined,
        depositPercentage: paymentData.depositPercentage ?? undefined,
        cashDiscountPercentage: paymentData.cashDiscountPercentage ?? undefined,
        // Use basePaymentTermId from paymentTerm object (new API structure)
        basePaymentTermId: paymentData.paymentTerm?.basePaymentTermId ?? undefined,
        dueInDays: paymentData.dueInDays ?? undefined,
        creditLimit: paymentData.creditLimit ?? undefined,
        // Update preference fields
        invoiceeContactId: data.invoiceeId ?? undefined,
        shippingInstruction: data.shippingInstruction || undefined,
        defaultShippingMarkFront: data.defaultShippingMarkFront || undefined,
        defaultShippingMarkSide: data.defaultShippingMarkSide || undefined,
      });

      handleSuccess(t('customer.messages.preferenceUpdatedSuccess'));
      onSuccess();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Override formStyles with generous spacing (matching EditBasicInfoModal)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized section title
  const sectionTitleStyle = formStyles.sectionTitleStyle;

  // Override label for tighter spacing and secondary color
  const labelStyle = {
    ...formStyles.labelStyle,
    color: token.colorTextSecondary,
    marginBottom: '4px',
  };

  // Filter active contacts for dropdown
  const activeContacts = useMemo(
    () => contacts.filter((contact) => contact.isActive),
    [contacts]
  );

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={t('customer.modals.editPreference')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('customer.actions.save')}
        cancelText={t('customer.actions.cancel')}
        confirmLoading={isSubmitting}
        width={800}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
          {/* Section 1: Billing Preference */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.billingPreference')}
            </Text>

            {/* Invoicee */}
            <div>
              <Text style={labelStyle}>{t('customer.fields.invoicee')}</Text>
              <Controller
                name="invoiceeId"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      onChange={(value) => field.onChange(value ?? null)}
                      size="large"
                      placeholder={t('customer.placeholders.invoicee')}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={activeContacts.map((contact) => ({
                        label: contact.name,
                        value: contact.contactId,
                      }))}
                      status={errors.invoiceeId ? 'error' : undefined}
                      style={{
                        ...formStyles.inputStyle,
                        marginTop: '4px',
                      }}
                    />
                    {errors.invoiceeId && touchedFields.invoiceeId && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {errors.invoiceeId.message}
                      </Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {/* Section 2: Shipping & Delivery */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.shippingDelivery')}
            </Text>

            {/* Shipping Instruction - full width */}
            <div style={{ marginBottom: '16px' }}>
              <Text style={labelStyle}>{t('customer.fields.shippingInstruction')}</Text>
              <Controller
                name="shippingInstruction"
                control={control}
                render={({ field }) => (
                  <>
                    <TextArea
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.shippingInstruction')}
                      rows={2}
                      style={{
                        ...formStyles.inputStyle,
                        marginTop: '4px',
                        borderColor: errors.shippingInstruction ? token.colorError : undefined,
                      }}
                    />
                    {errors.shippingInstruction && touchedFields.shippingInstruction && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {errors.shippingInstruction.message}
                      </Text>
                    )}
                  </>
                )}
              />
            </div>

            {/* Default Shipping Mark Front + Side - side by side */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.defaultShippingMarkFront')}</Text>
                <Controller
                  name="defaultShippingMarkFront"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextArea
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.shippingMarkFront')}
                        style={{
                          ...formStyles.inputStyle,
                          marginTop: '4px',
                          height: '128px',
                          borderColor: errors.defaultShippingMarkFront
                            ? token.colorError
                            : undefined,
                        }}
                      />
                      {errors.defaultShippingMarkFront && touchedFields.defaultShippingMarkFront && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {errors.defaultShippingMarkFront.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.defaultShippingMarkSide')}</Text>
                <Controller
                  name="defaultShippingMarkSide"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextArea
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.shippingMarkSide')}
                        style={{
                          ...formStyles.inputStyle,
                          marginTop: '4px',
                          height: '128px',
                          borderColor: errors.defaultShippingMarkSide
                            ? token.colorError
                            : undefined,
                        }}
                      />
                      {errors.defaultShippingMarkSide && touchedFields.defaultShippingMarkSide && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {errors.defaultShippingMarkSide.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default EditPreferenceModal;
